import { Component, OnInit } from '@angular/core';
import { RecordingSettings, DispositionDTO, ManualRecordingSettings, View } from 'src/app/recordings/recordings.model';
import { RecordingsMainService } from '../recordings-main.service';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

@Component({
  selector: 'cv-recordings-landing',
  templateUrl: './recordings-landing.component.html',
  styleUrls: ['./recordings-landing.component.scss']
})
export class RecordingsLandingComponent implements OnInit {
    public isLoading: boolean = false;
    public areFlagsLoading: boolean = false;
    public areDispositionsLoading: boolean = false;
    public submittingManualRecording: boolean = false;

    public recordingSettings: RecordingSettings;
    public manualRecordingSettings: ManualRecordingSettings;

    public sixMonthsAgo = new Date().addMonths(-6);

    public dispositions: DispositionDTO[];

    private modalRef: NgbModalRef;

    // Options used for ngbModal
    private readonly ngbModalOptions: NgbModalOptions = {
        animation: true,
        backdrop: 'static',
        keyboard: false,
        size: 'modal-dialog-noborder-when-loading',
    };

    constructor(
        public recordingsMainService: RecordingsMainService,
        private cvLocalizationFactory: CVLocalizationService,
        private ngbModal: NgbModal,
    ) {
    }

    async ngOnInit() {
        this.cvLocalizationFactory.GetSection("Recordings").then((returnObj) => {
            this.recordingsMainService.localization = returnObj;
        }).catch((error) => {
            console.log(error);
        });

        // Timestamps for these dates need to be set to the very 'start' of that day, or time 00:00:00
        let endDate = new Date(new Date().toDateString());
        let startDate = new Date(endDate).addDays(-7);

        this.recordingSettings = {
            StartDate: startDate,
            EndDate: endDate,
            LengthMin: "",
            LengthMax: "",
            ContactType: 0,
            ContactStatus: 0,
            ContactId: "",
            ContactSource: "",
            OtherId: "",
            SelectedDisposition: "All",
            SelectedFlag: "0",
            ResultsLimit: 30,
            IsRandomized: false,
            RedialRadio: 0,
            OccurMin: "0"
        };

        try {
            this.isLoading = true;

            this.recordingsMainService.loadDetailSecurity().then((returnObj) => {
                this.recordingsMainService.accessData = returnObj;
            }).catch((error) => {
                console.log(error);
            });
            this.recordingsMainService.groupLists = await this.recordingsMainService.loadGroupLists();
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isLoading = false;
        }

        this.areFlagsLoading = true;
        this.recordingsMainService.loadAllFlags().then((returnObj) => {
            this.recordingsMainService.flags = returnObj;
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            this.areFlagsLoading = false;
        });

        this.areDispositionsLoading = true;
        this.recordingsMainService.loadAllDispositions().then((returnObj) => {
            this.dispositions = returnObj;
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            this.areDispositionsLoading = false;
        });
    }

    /**
     * Populates list of recordings based on provided settings object.
     * @param settings RecordingSettings object containing filter values
     */
    public async loadRecordingList(settings: RecordingSettings = this.recordingSettings, manual = false): Promise<void> {
        // Validate method
        if (!this.validateSettings())
            return;

        try {
            this.isLoading = true;

            this.recordingsMainService.recordings = await this.recordingsMainService.loadRecordings(settings, manual);

            this.recordingsMainService.changeView(View.ResultList);
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isLoading = false;
        }
    }

    /**
     * Returns true if the current filter settings are valid to perform a load, false if otherwise.
     * */
    public validateSettings(): boolean {
        if (this.recordingSettings.StartDate == null ||
            this.recordingSettings.EndDate == null ||
            this.recordingSettings.StartDate > this.recordingSettings.EndDate ||
            this.recordingSettings.ContactType < 0 || this.recordingSettings.ContactType > 2 ||
            this.recordingSettings.ContactStatus < 0 || this.recordingSettings.ContactStatus > 2 ||
            isNaN(Number(this.recordingSettings.ContactId)) ||
            isNaN(Number(this.recordingSettings.OtherId)) ||
            this.recordingSettings.RedialRadio < 0 || this.recordingSettings.RedialRadio > 2 ||
            isNaN(Number(this.recordingSettings.OccurMin)) ||
            !this.validateTimeType(this.recordingSettings.LengthMin) || !this.validateTimeType(this.recordingSettings.LengthMax)
        )
            return false;

        return true;
    }

    /**
     * Returns true if inputted string represents a number within the Bigint range (-9223372036854775808 to 9223372036854775807). Also returns true if string is null.
     * @param value String value
     */
    public valueWithinBigIntRange(value: string) {
        if (this.recordingsMainService.isNotANumber(value))
            return false;

        if (!value)
            return true;

        let number = BigInt(value);
        return BigInt("-9223372036854775808") <= number && number <= BigInt("9223372036854775807");
    }

    /**
     * Returns true if the recordingSettings.LengthMin value is formatted to a valid time type.
     * */
    public validateTimeType(value: string): boolean {
        let regex = /^(?:2[0-3]|[01]?\d):[0-5]\d:[0-5]\d$/;
        if (value != "" && RegExp(regex).exec(value) == null)
            return false;

        return true;
    }

    /**
     * Resets variables related to the manual recording creation popup to their default values and then opens the popup.
     * @param template ngTemplate for the popup
     */
    public async openManualRecordingPopup(template) {
        let recLevel = this.recordingsMainService.groupLists.RecLevels.length > 0 ? this.recordingsMainService.groupLists.RecLevels[0].GroupLevel : 0;

        // Clear inputs
        this.manualRecordingSettings = {
            EmpGroupId: this.recordingsMainService.groupLists.Emp1Groups.length > 0 ? this.recordingsMainService.groupLists.Emp1Groups[0].GroupID : 0,
            RecLevel: recLevel,
            RecGroupId: this.recordingsMainService.groupLists.RecGroupsDictionary[recLevel] != null && this.recordingsMainService.groupLists.RecGroupsDictionary[recLevel].length > 0
                ? this.recordingsMainService.groupLists.RecGroupsDictionary[recLevel][0].GroupID
                : 0,
            ContactDate: new Date(),
            ContactId: null,
            Phone: "",
            Disposition: "",
            OtherId: "",
            Notes: ""
        };

        //this.navigationService.showOverlay = true; // Not sure if I need this part
        this.modalRef = this.ngbModal.open(template, this.ngbModalOptions);
    }

    /**
     * Validates manual form being created. Returns true if form passes validation, false if not.
     * */
    private validateAddRecordingForm(): boolean {
        // This shows the "Setting is Invalid" message on the Contact ID field if it wasn't filled out
        if (this.manualRecordingSettings.ContactId == null)
            this.manualRecordingSettings.ContactId = "";

        if (
            this.manualRecordingSettings.EmpGroupId == null || this.manualRecordingSettings.EmpGroupId <= 0 ||
            this.manualRecordingSettings.RecLevel == null || this.manualRecordingSettings.RecLevel <= 0 ||
            this.manualRecordingSettings.RecGroupId == null || this.manualRecordingSettings.RecGroupId <= 0 ||
            this.manualRecordingSettings.ContactDate == null ||
            this.manualRecordingSettings.ContactId == "" || !this.valueWithinBigIntRange(this.manualRecordingSettings.ContactId)
        )
            return false;

        return true;
    }

    /**
     * Closes the manual recording creation popup.
     * */
    public closeManualRecordingPopup() {
        this.modalRef.close();
    }

    /**
     * Saves the manual recording form and closes the popup.
     * */
    public async saveManualRecording() {
        // Validate settings and prevent potential double submissions
        if (!this.validateAddRecordingForm() || this.submittingManualRecording)
            return;

        try {
            this.submittingManualRecording = true;

            let newRecordingId = await this.recordingsMainService.addManualRecording(this.manualRecordingSettings);

            // Pull new recording from database as if it were a normal load and open it in the details view
            let pullSettings: RecordingSettings = {
                StartDate: new Date(this.manualRecordingSettings.ContactDate).addMinutes(-1),
                EndDate: new Date(this.manualRecordingSettings.ContactDate).addMinutes(1),
                LengthMin: "",
                LengthMax: "",
                ContactType: 0,
                ContactStatus: 0,
                ContactId: this.manualRecordingSettings.ContactId,
                ContactSource: "",
                OtherId: this.manualRecordingSettings.OtherId,
                SelectedDisposition: this.manualRecordingSettings.Disposition,
                SelectedFlag: "0",
                ResultsLimit: 0,
                IsRandomized: false,
                RedialRadio: 0,
                OccurMin: "0"
            };
            await this.loadRecordingList(pullSettings, true);

            this.recordingsMainService.changeView(View.Details, this.recordingsMainService.recordings.find(o => o.RecordingID == newRecordingId));
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.submittingManualRecording = false;
            this.modalRef.close();
        }
    }

    /**
     * Updates the RecGroupId field in the manual recording creation settings.
     * */
    public updateSelectedRecGroupId(): void {
        if (this.recordingsMainService.groupLists.RecGroupsDictionary[this.manualRecordingSettings.RecLevel]?.length > 0)
            this.manualRecordingSettings.RecGroupId = this.recordingsMainService.groupLists.RecGroupsDictionary[this.manualRecordingSettings.RecLevel][0].GroupID;
        else
            this.manualRecordingSettings.RecGroupId = 0;
    }
}
